import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Typography,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Chip,
  Popover,
} from '@mui/material';
import {
  Edit,
  Delete,
  ArrowUpward,
  ArrowDownward,
  ContactPhone,
  ContentCopy,
  Check,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const CURRENCY_OPTIONS = ['USD', 'UAH', 'EUR'];

const PhoneCatalogTable = ({
  entries,
  loading,
  page,
  totalPages,
  onPageChange,
  onInlineEdit,
  onToggleTop,
  onToggleFreeForTrial,
  onEditClick,
  onDeleteClick,
  sortField,
  sortOrder,
  onSortChange,
  selectedIds = [],
  onSelectionChange,
}) => {
  const { t } = useTranslation();
  const [editingCell, setEditingCell] = useState(null); // { id, field }
  const [editValue, setEditValue] = useState('');
  const [docsPopover, setDocsPopover] = useState({ anchorEl: null, text: '', entryId: null });
  const [copied, setCopied] = useState(false);

  const startEdit = useCallback((id, field, currentValue) => {
    setEditingCell({ id, field });
    setEditValue(currentValue ?? '');
  }, []);

  const commitEdit = useCallback(() => {
    if (!editingCell) return;
    const entry = entries.find((e) => e.id === editingCell.id);
    if (entry && String(entry[editingCell.field]) !== String(editValue)) {
      onInlineEdit(editingCell.id, { [editingCell.field]: editValue });
    }
    setEditingCell(null);
    setEditValue('');
  }, [editingCell, editValue, entries, onInlineEdit]);

  const cancelEdit = useCallback(() => {
    setEditingCell(null);
    setEditValue('');
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') commitEdit();
      if (e.key === 'Escape') cancelEdit();
    },
    [commitEdit, cancelEdit]
  );

  const isEditing = (id, field) =>
    editingCell?.id === id && editingCell?.field === field;

  const allPageSelected = entries.length > 0 && entries.every((e) => selectedIds.includes(e.id));
  const somePageSelected = entries.some((e) => selectedIds.includes(e.id)) && !allPageSelected;

  const handleHeaderCheckbox = () => {
    if (allPageSelected) {
      onSelectionChange(selectedIds.filter((id) => !entries.find((e) => e.id === id)));
    } else {
      const pageIds = entries.map((e) => e.id);
      onSelectionChange([...new Set([...selectedIds, ...pageIds])]);
    }
  };

  const handleRowCheckbox = (id) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((sid) => sid !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  const SortableHeader = ({ field, children, sx: sxProp }) => {
    const active = sortField === field;
    return (
      <TableCell
        sx={{
          fontWeight: 600,
          color: 'text.primary',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { color: '#00C853' },
          whiteSpace: 'nowrap',
          ...sxProp,
        }}
        onClick={() => onSortChange(field)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {children}
          {active &&
            (sortOrder === 'asc' ? (
              <ArrowUpward sx={{ fontSize: 14 }} />
            ) : (
              <ArrowDownward sx={{ fontSize: 14 }} />
            ))}
        </Box>
      </TableCell>
    );
  };

  const renderEditableCell = (entry, field, type = 'text', width) => {
    if (isEditing(entry.id, field)) {
      if (type === 'select-currency') {
        return (
          <TableCell sx={{ width }}>
            <Select
              size="small"
              value={editValue}
              onChange={(e) => {
                setEditValue(e.target.value);
                onInlineEdit(entry.id, { [field]: e.target.value });
                setEditingCell(null);
              }}
              onClose={() => setEditingCell(null)}
              autoFocus
              open
              sx={{ fontSize: 13, height: 30, minWidth: 70 }}
            >
              {CURRENCY_OPTIONS.map((o) => (
                <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
                  {o}
                </MenuItem>
              ))}
            </Select>
          </TableCell>
        );
      }
      return (
        <TableCell sx={{ width }}>
          <TextField
            size="small"
            type={type}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleKeyDown}
            autoFocus
            sx={{
              '& .MuiInputBase-input': { fontSize: 13, py: 0.5, px: 1 },
              '& .MuiOutlinedInput-root': { height: 30 },
            }}
          />
        </TableCell>
      );
    }

    const value = entry[field];
    const isFeeField = ['activation_fee', 'monthly_fee', 'internal_activation_fee', 'internal_monthly_fee'].includes(field);
    const rawField = field + '_raw';

    return (
      <TableCell
        sx={{
          width,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(0,200,83,0.04)' },
        }}
        onClick={() => startEdit(entry.id, field, value)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isFeeField ? (
            <Tooltip title={entry[rawField] || ''} arrow>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontSize: 13 }}>
                  {value != null ? Number(value).toFixed(2) : '—'}
                </Typography>
                {entry.is_from_price && (
                  <Chip
                    label="FROM"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: '#fff3e0',
                      color: '#e65100',
                    }}
                  />
                )}
              </Box>
            </Tooltip>
          ) : (
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              {value || '—'}
            </Typography>
          )}
        </Box>
      </TableCell>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: 'none',
        overflow: 'hidden',
        bgcolor: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        minHeight: 400,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <TableContainer sx={{ flex: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8f9fa' }}>
              <TableCell sx={{ width: 48, py: 0 }}>
                <Checkbox
                  checked={allPageSelected}
                  indeterminate={somePageSelected}
                  onChange={handleHeaderCheckbox}
                  size="small"
                  sx={{ '&.Mui-checked': { color: '#00C853' }, '&.MuiCheckbox-indeterminate': { color: '#00C853' } }}
                />
              </TableCell>
              <SortableHeader field="country" sx={{ width: 120 }}>
                {t('phoneCatalog.country', 'Country')}
              </SortableHeader>
              <SortableHeader field="city" sx={{ width: 140 }}>
                {t('phoneCatalog.city', 'City')}
              </SortableHeader>
              <SortableHeader field="provider" sx={{ width: 120 }}>
                {t('phoneCatalog.provider', 'Provider')}
              </SortableHeader>
              <SortableHeader field="code" sx={{ width: 100 }}>
                {t('phoneCatalog.code', 'Code')}
              </SortableHeader>
              <SortableHeader field="channels" sx={{ width: 110 }}>
                {t('phoneCatalog.channels', 'Channels')}
              </SortableHeader>
              <SortableHeader field="activation_fee" sx={{ width: 110 }}>
                {t('phoneCatalog.activationFee', 'Activation Fee')}
              </SortableHeader>
              <SortableHeader field="monthly_fee" sx={{ width: 110 }}>
                {t('phoneCatalog.monthlyFee', 'Monthly Fee')}
              </SortableHeader>
              <SortableHeader field="internal_activation_fee" sx={{ width: 100 }}>
                Int. Activation
              </SortableHeader>
              <SortableHeader field="internal_monthly_fee" sx={{ width: 100 }}>
                Int. Monthly
              </SortableHeader>
              <SortableHeader field="currency" sx={{ width: 80 }}>
                {t('phoneCatalog.currency', 'Currency')}
              </SortableHeader>
              <SortableHeader field="incoming_cost" sx={{ width: 120 }}>
                {t('phoneCatalog.incomingCost', 'Incoming Cost')}
              </SortableHeader>
              <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: 160 }}>
                {t('phoneCatalog.requiredDocs', 'Req. Documents')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: 160 }}>
                {t('phoneCatalog.comment', 'Comment')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: 60 }}>
                {t('phoneCatalog.top', 'Top')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: 80 }}>
                {t('phoneCatalog.freeForTrial', 'Free trial')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: 80 }}>
                {t('phoneCatalog.actions', 'Actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={17} sx={{ textAlign: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: '#00C853' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {t('phoneCatalog.loading', 'Loading catalog...')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={17} sx={{ textAlign: 'center', py: 8 }}>
                  <ContactPhone sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                    {t('phoneCatalog.noEntries', 'No entries found')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('phoneCatalog.adjustFilters', 'Try adjusting your filters')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => (
                <TableRow
                  key={entry.id}
                  sx={{
                    '&:hover': { bgcolor: 'rgba(0, 200, 83, 0.02)' },
                    bgcolor: selectedIds.includes(entry.id) ? 'rgba(0, 200, 83, 0.04)' : undefined,
                  }}
                >
                  {/* Selection checkbox */}
                  <TableCell sx={{ width: 48, py: 0 }}>
                    <Checkbox
                      checked={selectedIds.includes(entry.id)}
                      onChange={() => handleRowCheckbox(entry.id)}
                      size="small"
                      sx={{ '&.Mui-checked': { color: '#00C853' } }}
                    />
                  </TableCell>

                  {/* Country — not editable */}
                  <TableCell sx={{ width: 120 }}>
                    <Typography variant="body2" sx={{ fontSize: 13 }}>
                      {entry.country}
                    </Typography>
                  </TableCell>

                  {renderEditableCell(entry, 'city', 'text', 140)}

                  {/* Provider — not editable */}
                  <TableCell sx={{ width: 120 }}>
                    <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>
                      {entry.provider}
                    </Typography>
                  </TableCell>

                  {renderEditableCell(entry, 'code', 'text', 100)}
                  {renderEditableCell(entry, 'channels', 'text', 110)}
                  {renderEditableCell(entry, 'activation_fee', 'number', 110)}
                  {renderEditableCell(entry, 'monthly_fee', 'number', 110)}
                  {renderEditableCell(entry, 'internal_activation_fee', 'number', 100)}
                  {renderEditableCell(entry, 'internal_monthly_fee', 'number', 100)}
                  {renderEditableCell(entry, 'currency', 'select-currency', 80)}
                  {renderEditableCell(entry, 'incoming_cost', 'text', 120)}

                  {/* Required Documents — truncated, click opens popover */}
                  <TableCell
                    sx={{ width: 160, cursor: entry.required_documents ? 'pointer' : 'default', '&:hover': entry.required_documents ? { bgcolor: 'rgba(25,118,210,0.04)' } : {} }}
                    onClick={(e) => {
                      if (entry.required_documents) {
                        setDocsPopover({ anchorEl: e.currentTarget, text: entry.required_documents, entryId: entry.id });
                        setCopied(false);
                      } else {
                        startEdit(entry.id, 'required_documents', entry.required_documents);
                      }
                    }}
                  >
                    {isEditing(entry.id, 'required_documents') ? (
                      <TextField
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={commitEdit}
                        onKeyDown={handleKeyDown}
                        autoFocus
                        multiline
                        maxRows={3}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12 }, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: 12, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: entry.required_documents ? '#1976d2' : 'text.disabled' }}>
                        {entry.required_documents || '—'}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Comment — truncated with tooltip */}
                  <TableCell
                    sx={{ width: 160, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,200,83,0.04)' } }}
                    onClick={() => startEdit(entry.id, 'comment', entry.comment)}
                  >
                    {isEditing(entry.id, 'comment') ? (
                      <TextField
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={commitEdit}
                        onKeyDown={handleKeyDown}
                        autoFocus
                        multiline
                        maxRows={3}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12 }, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                      />
                    ) : (
                      <Tooltip title={entry.comment || ''} arrow>
                        <Typography variant="body2" sx={{ fontSize: 12, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: entry.comment ? 'text.primary' : 'text.disabled' }}>
                          {entry.comment || '—'}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>

                  {/* Top checkbox */}
                  <TableCell sx={{ width: 60 }}>
                    <Checkbox
                      checked={entry.is_top_country || false}
                      onChange={() => onToggleTop(entry.id)}
                      size="small"
                      sx={{ '&.Mui-checked': { color: '#00C853' } }}
                    />
                  </TableCell>

                  {/* Free for trial checkbox */}
                  <TableCell sx={{ width: 80 }}>
                    <Checkbox
                      checked={entry.is_free_for_trial || false}
                      onChange={() => onToggleFreeForTrial(entry.id)}
                      size="small"
                      sx={{ '&.Mui-checked': { color: '#00C853' } }}
                    />
                  </TableCell>

                  {/* Actions */}
                  <TableCell sx={{ width: 80 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title={t('phoneCatalog.edit', 'Edit')}>
                        <IconButton
                          size="small"
                          onClick={() => onEditClick(entry)}
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            border: '1px solid #e5e7eb',
                            bgcolor: 'white',
                            color: '#6b7280',
                            '&:hover': {
                              bgcolor: '#f3f4f6',
                              borderColor: '#d1d5db',
                              color: '#00C853',
                            },
                          }}
                        >
                          <Edit sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('phoneCatalog.delete', 'Delete')}>
                        <IconButton
                          size="small"
                          onClick={() => onDeleteClick(entry)}
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            border: '1px solid #e5e7eb',
                            bgcolor: 'white',
                            color: '#6b7280',
                            '&:hover': {
                              bgcolor: '#fef2f2',
                              borderColor: '#fecaca',
                              color: '#dc2626',
                            },
                          }}
                        >
                          <Delete sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 3,
            borderTop: '1px solid rgba(0, 0, 0, 0.06)',
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => onPageChange(newPage)}
            color="primary"
            size="large"
            sx={{
              '& .MuiPaginationItem-root': {
                color: '#6b7280',
                fontWeight: 500,
                borderRadius: 1,
                '&.Mui-selected': {
                  backgroundColor: '#00C853',
                  color: 'white',
                  '&:hover': { backgroundColor: '#00A843' },
                },
                '&:hover': { backgroundColor: '#f3f4f6' },
              },
            }}
          />
        </Box>
      )}
      {/* Required Documents Popover */}
      <Popover
        open={Boolean(docsPopover.anchorEl)}
        anchorEl={docsPopover.anchorEl}
        onClose={() => setDocsPopover({ anchorEl: null, text: '', entryId: null })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { maxWidth: 450, maxHeight: 400, borderRadius: 1 } } }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1976d2' }}>
              {t('calculator.requiredDocs', 'Required documents')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(docsPopover.text);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  } catch { /* fallback */ }
                }}
              >
                {copied
                  ? <Check sx={{ fontSize: 16, color: '#00C853' }} />
                  : <ContentCopy sx={{ fontSize: 16, color: 'rgba(0,0,0,0.4)' }} />}
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  setDocsPopover({ anchorEl: null, text: '', entryId: null });
                  startEdit(docsPopover.entryId, 'required_documents', docsPopover.text);
                }}
              >
                <Edit sx={{ fontSize: 16, color: 'rgba(0,0,0,0.4)' }} />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ fontSize: 13, lineHeight: 1.6, color: 'rgba(0,0,0,0.75)', overflowY: 'auto', maxHeight: 320 }}>
            {docsPopover.text.split('\n').map((line, i) => {
              const isSectionHeader = /^\[.+\]$/.test(line.trim());
              const isLabel = /^(Personal|Business|Address):/.test(line.trim());
              const isBullet = line.trim().startsWith('•');
              if (!line.trim()) return <Box key={i} sx={{ height: 8 }} />;
              if (isSectionHeader) {
                return (
                  <Box key={i} sx={{ mt: i > 0 ? 1.5 : 0, mb: 0.5, fontWeight: 700, color: '#1976d2', fontSize: 13 }}>
                    {line.replace(/[[\]]/g, '')}
                  </Box>
                );
              }
              return (
                <Box key={i} sx={{
                  mb: 0.2,
                  pl: isBullet ? 2 : 0,
                  fontWeight: isLabel ? 600 : 400,
                  color: isLabel ? 'rgba(0,0,0,0.87)' : 'rgba(0,0,0,0.7)',
                }}>
                  {line}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Popover>
    </Paper>
  );
};

export default PhoneCatalogTable;
